import { useState, useEffect } from "react";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

/**
 * useActivityFeed — real-time listener for the activityFeed collection.
 *
 * Returns up to `limit` recent entries for the selected child, sorted by
 * loggedAt descending. Sorting is done client-side to avoid requiring a
 * composite Firestore index on (childId, loggedAt).
 *
 * Each entry shape:
 *   { id, childId, collectionName, icon, label, summary,
 *     eventTime, loggedAt, loggedBy: { uid, name, email } }
 */
export function useActivityFeed(childId, limit = 30) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.uid || !childId) {
      setItems([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "activityFeed"),
      where("childId", "==", childId)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        // Sort newest first, then cap at limit
        raw.sort((a, b) => {
          const ta = new Date(a.loggedAt || 0).getTime();
          const tb = new Date(b.loggedAt || 0).getTime();
          return tb - ta;
        });
        setItems(raw.slice(0, limit));
        setLoading(false);
      },
      (err) => {
        console.error("Activity feed error:", err);
        setLoading(false);
      }
    );

    return unsub;
  }, [user?.uid, childId, limit]);

  return { items, loading };
}
